// =============================================================================
// Auth error classification
//
// Small pure predicates for reasoning about Supabase AuthError shapes, kept
// out of the page components so they can be unit-tested without a DOM. Each
// checks the typed `code` first and falls back to a message-substring match
// for older SDKs / unknown shapes.
// =============================================================================

interface AuthErrorLike {
  code?: string
  message?: string
}

/**
 * Sign-in was rejected because the email hasn't been confirmed yet — we show a
 * specific message + a "resend verification" affordance.
 */
export function isEmailNotConfirmedError(err: AuthErrorLike | null): boolean {
  if (!err) return false
  if (err.code === 'email_not_confirmed') return true
  return /email\s*not\s*confirmed/i.test(err.message ?? '')
}

/**
 * A magic-link request failed because the email has no account. With
 * `shouldCreateUser: false`, Supabase rejects unknown emails (`otp_disabled` /
 * "Signups not allowed for otp"). We treat this exactly like success so the UI
 * never reveals whether an email is registered.
 */
export function isNonexistentUserError(err: AuthErrorLike | null): boolean {
  if (!err) return false
  if (err.code === 'otp_disabled') return true
  return /signups?\s+not\s+allowed|user\s+not\s+found/i.test(err.message ?? '')
}
